import {useMemo} from 'react'
import {ISchema} from 'libs/yup'
import {FieldError, FieldErrors, FieldValues} from 'react-hook-form'
import {Section} from 'jbc-front/components/presenters/form/Section'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'

const ORDINAL_FORMAT_DEFAULT = 'の$number番目'

interface FormError {
  path: string
  message: string
}

interface FormErrorsProps<T> {
  errors: FieldErrors
  isSubmitting: boolean
  schema?: ISchema<T>
  prependFullPath?: boolean
}

export const FormErrors = <T,>({errors, isSubmitting, schema, prependFullPath}: FormErrorsProps<T>) => {
  const formErrors = useMemo(() => (isSubmitting ? [] : [...flattenErrors(errors)]), [isSubmitting])

  return formErrors.length > 0 ? (
    <Section title="入力内容に誤りがあります" error>
      {formErrors.map((error, index) => {
        let message = error.message
        if (schema && prependFullPath) {
          message = prependFullPathMessage(error, schema)
        }

        return <ErrorMessage key={index}>{message}</ErrorMessage>
      })}
    </Section>
  ) : (
    <></>
  )
}

const isFieldError = (obj: object): obj is FieldError => 'type' in obj && 'message' in obj

const flattenErrors = function* <TFieldValues extends FieldValues>(
  errors: FieldErrors<TFieldValues>,
  path: (string | number)[] = []
): Generator<FormError> {
  if (Array.isArray(errors)) {
    for (let index = 0; index < errors.length; index++) {
      if (errors[index]) {
        yield* flattenErrors(errors[index], [...path, index])
      }
    }
  } else if (isFieldError(errors)) {
    yield {
      ...errors,
      path: path.reduce((p, c) => `${p}.${c}`) as string,
      message: errors.message || '',
    }
  } else {
    for (const key in errors) {
      yield* flattenErrors(errors[key] as FieldErrors, [...path, key])
    }
  }
}

const prependFullPathMessage = <T,>(error: FormError, schema: ISchema<T>): string => {
  const readableAncestors: string[] = []

  let fd = schema.describe()
  const paths = error.path.split('.')
  for (const segment of paths.slice(0, -1)) {
    // labelが設定されている場合取り込む
    if ('label' in fd && fd.label) {
      let label: string
      if (fd.type === 'array') {
        const format = fd.meta?.formErrors?.ordinalFormat ?? ORDINAL_FORMAT_DEFAULT
        const number = `${Number(segment) + 1}`
        label = `${fd.label}${format.replaceAll('$number', number)}の`
      } else {
        label = `${fd.label}の`
      }
      readableAncestors.push(label)
    }

    // 次に参照するFieldDescriptionを取得
    if ('fields' in fd && segment in fd.fields) {
      fd = fd.fields[segment] || fd
    }
  }

  if (readableAncestors.length) {
    const ancestor = readableAncestors.join()
    return `${ancestor}${error.message}`
  } else {
    return error.message
  }
}
